/**
 * passkeyLogin.ts
 *
 * Passkey sign-in on botspot.trade itself, without the hosted Auth0 page.
 *
 * WHY THIS EXISTS:
 * - Rob asked for "Continue with a passkey" to stay on the BotSpot site.
 * - Auth0's passkey API (`/passkey/challenge` plus the webauthn token grant)
 *   only answers on the tenant's custom login domain, never `*.auth0.com`, so
 *   it is configured separately as REACT_APP_AUTH0_PASSKEY_DOMAIN.
 * - Auth0 checks the passkey; Node's existing `/auth/embedded/auth0-session`
 *   checks the signed tokens and issues the BotSpot session, exactly as the
 *   Google and email paths do. There is no second session store.
 *
 * Known BotSpot tenants use their verified custom domain by default. Missing
 * configuration on another tenant stays an inline error, never a hosted restart.
 */
import { z } from 'zod';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import {
  decodeBase64Url,
  encodeBase64Url,
  getMyAccountAudience,
  getPasskeyDomain,
  MY_ACCOUNT_SCOPES,
} from './myAccountMfa';

const PASSKEY_REALM = 'Username-Password-Authentication';
const UNAVAILABLE_MESSAGE = 'Passkey sign-in is unavailable right now. Try email or Google.';

const challengeSchema = z.object({
  auth_session: z.string().min(1),
  authn_params_public_key: z.object({
    challenge: z.string().min(1),
    rpId: z.string().min(1),
    timeout: z.number().positive().optional(),
  }),
});

const tokenSchema = z.object({ access_token: z.string().min(1), id_token: z.string().min(1) });

const sessionSchema = z.object({
  token: z.string().min(1),
  isNewUser: z.boolean().optional(),
  postSignupJourneyRequired: z.boolean().optional(),
  user: z.object({ id: z.union([z.string(), z.number()]).optional() }).optional(),
  securityAuthorization: z
    .object({
      token: z.string().min(1),
      subject: z.string().min(1),
      email: z.string().min(1),
      expiresAt: z.number(),
      domain: z.string().optional(),
    })
    .optional(),
});

const errorSchema = z.object({ message: z.string().min(1) });

export type PasskeySession = z.infer<typeof sessionSchema>;

type SerializedPasskeyAssertion = {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment: string | undefined;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle: string | null;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
};

/** A message safe to show the customer, as opposed to a cancelled ceremony. */
export class PasskeyLoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PasskeyLoginError';
  }
}

/** Returns the custom login domain when native passkey sign-in is configured. */
export function nativePasskeyDomain(): string | null {
  return getPasskeyDomain(process.env.REACT_APP_AUTH0_DOMAIN || '');
}

type ChallengeBody = { client_id: string; realm: string };
type TokenBody = {
  grant_type: string;
  client_id: string;
  realm: string;
  audience: string;
  scope: string;
  auth_session: string;
  authn_response: SerializedPasskeyAssertion;
};
type SessionBody = {
  idToken: string;
  highRiskStepUpActionId?: string;
  security?: true;
  securityAccountToken?: string;
};

async function postJson<T>(
  url: string,
  body: ChallengeBody | TokenBody | SessionBody,
  schema: z.ZodType<T>,
  authorization: string | undefined,
  failureMessage: string,
  signal?: AbortSignal,
): Promise<T> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (authorization) headers.set('Authorization', authorization);
  const response = await fetchWithTimeout(
    url,
    { method: 'POST', headers, signal, body: JSON.stringify(body) },
    15000,
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const parsed = errorSchema.safeParse(data);
    throw new PasskeyLoginError(parsed.success ? parsed.data.message : failureMessage);
  }
  return schema.parse(data);
}

/**
 * Runs the passkey ceremony and returns the BotSpot session from Node.
 * A customer who closes the browser prompt gets a DOMException named
 * NotAllowedError or AbortError, which callers treat as a quiet cancel.
 */
export async function loginWithPasskey(
  apiBase: string,
  highRiskStepUpActionId?: string | null,
  signal?: AbortSignal,
  security?: { accountToken: string },
): Promise<PasskeySession> {
  const domain = nativePasskeyDomain();
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = security
    ? getMyAccountAudience(domain || '')
    : process.env.REACT_APP_AUTH0_AUDIENCE;
  if (!domain || !clientId || !audience) throw new PasskeyLoginError(UNAVAILABLE_MESSAGE);
  if (!window.isSecureContext || !window.PublicKeyCredential || !navigator.credentials?.get) {
    throw new PasskeyLoginError('This browser cannot use a passkey. Try email or Google.');
  }

  const challenge = await postJson(
    `https://${domain}/passkey/challenge`,
    { client_id: clientId, realm: PASSKEY_REALM },
    challengeSchema,
    undefined,
    UNAVAILABLE_MESSAGE,
    signal,
  );
  const options = challenge.authn_params_public_key;
  // SAFETY: Only a publicKey request is made, so the result is a PublicKeyCredential or null.
  const credential = (await navigator.credentials
    .get({
      publicKey: {
        challenge: decodeBase64Url(options.challenge),
        rpId: options.rpId,
        userVerification: 'required',
        timeout: Math.min(options.timeout || 60000, 60000),
      },
      signal,
    })
    .catch((error: DOMException) => {
      // The browser owns RP validation, including standard Related Origin Requests.
      // Never rewrite the provider's RP ID or bypass a rejected origin.
      if (error instanceof DOMException && error.name === 'SecurityError') {
        throw new PasskeyLoginError(
          'Passkey sign-in is unavailable on this address. Use email or Google, then manage passkeys in Security settings.',
        );
      }
      throw error;
    })) as PublicKeyCredential | null;
  if (!credential) throw new DOMException('Passkey sign-in cancelled', 'NotAllowedError');
  // SAFETY: credentials.get(publicKey) returns an assertion, never an attestation.
  const assertion = credential.response as AuthenticatorAssertionResponse;
  const authnResponse: SerializedPasskeyAssertion = {
    id: credential.id,
    rawId: encodeBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    response: {
      authenticatorData: encodeBase64Url(assertion.authenticatorData),
      clientDataJSON: encodeBase64Url(assertion.clientDataJSON),
      signature: encodeBase64Url(assertion.signature),
      userHandle: assertion.userHandle ? encodeBase64Url(assertion.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  const tokens = await postJson(
    `https://${domain}/oauth/token`,
    {
      grant_type: 'urn:okta:params:oauth:grant-type:webauthn',
      client_id: clientId,
      realm: PASSKEY_REALM,
      audience,
      scope: security ? `openid profile email ${MY_ACCOUNT_SCOPES}` : 'openid profile email',
      auth_session: challenge.auth_session,
      authn_response: authnResponse,
    },
    tokenSchema,
    undefined,
    'That passkey did not work. Try again, or use email or Google.',
    signal,
  );

  const sessionBody: SessionBody = highRiskStepUpActionId
    ? { idToken: tokens.id_token, highRiskStepUpActionId }
    : { idToken: tokens.id_token };
  if (security) {
    sessionBody.security = true;
    sessionBody.securityAccountToken = security.accountToken;
  }
  const session = await postJson(
    `${apiBase}/auth/embedded/auth0-session`,
    sessionBody,
    sessionSchema,
    `Bearer ${tokens.access_token}`,
    'We could not complete sign-in. Please try again.',
    signal,
  );
  if (security && !session.securityAuthorization) {
    throw new PasskeyLoginError(
      'We could not verify access to security settings. Please try again.',
    );
  }
  return session;
}
